import type { CSSProperties, MouseEvent } from 'react';
import { createElement, useEffect, useRef, useState } from 'react';
import { figmaColorScales } from '../../../core/tokens';
import { kaiTypographyWeb, pxToRem } from '../../typography';
import type {
  AdminChromeTopNavItem,
  AdminChromeTopNavMenuItem,
  AdminChromeTopNavTabsProps,
} from './types';

type InteractiveVisualState =
  | 'default'
  | 'hover'
  | 'pressed'
  | 'selected'
  | 'selectedHover'
  | 'selectedPressed';

const TOP_NAV_COLORS = {
  active: figmaColorScales.yellow[500],
  defaultText: figmaColorScales.neutral[400],
  hoverText: figmaColorScales.neutral[50],
  hoverBackground: figmaColorScales.neutral[800],
  pressedBackground: figmaColorScales.neutral[700],
} as const;

const TOP_NAV_MENU_COLORS = {
  surface: figmaColorScales.neutral[800],
  defaultText: figmaColorScales.neutral[400],
  hoverText: figmaColorScales.neutral[50],
  hoverBackground: figmaColorScales.neutral[700],
  pressedBackground: figmaColorScales.neutral[600],
  selectedHoverBackground: figmaColorScales.neutral[900],
  selectedPressedBackground: '#000000',
  active: figmaColorScales.yellow[500],
} as const;

const TOP_NAV_TAB_STYLES = {
  default: {
    background: 'transparent',
    color: TOP_NAV_COLORS.defaultText,
  },
  hover: {
    background: TOP_NAV_COLORS.hoverBackground,
    color: TOP_NAV_COLORS.hoverText,
  },
  pressed: {
    background: TOP_NAV_COLORS.pressedBackground,
    color: TOP_NAV_COLORS.hoverText,
  },
  selected: {
    background: 'transparent',
    color: TOP_NAV_COLORS.active,
  },
  selectedHover: {
    background: TOP_NAV_COLORS.hoverBackground,
    color: TOP_NAV_COLORS.active,
  },
  selectedPressed: {
    background: TOP_NAV_COLORS.pressedBackground,
    color: TOP_NAV_COLORS.active,
  },
} as const satisfies Record<InteractiveVisualState, { background: string; color: string }>;

const TOP_NAV_MENU_ITEM_STYLES = {
  default: {
    background: TOP_NAV_MENU_COLORS.surface,
    color: TOP_NAV_MENU_COLORS.defaultText,
    text: kaiTypographyWeb.small,
  },
  hover: {
    background: TOP_NAV_MENU_COLORS.hoverBackground,
    color: TOP_NAV_MENU_COLORS.hoverText,
    text: kaiTypographyWeb.small,
  },
  pressed: {
    background: TOP_NAV_MENU_COLORS.pressedBackground,
    color: TOP_NAV_MENU_COLORS.hoverText,
    text: kaiTypographyWeb.smallBold,
  },
  selected: {
    background: TOP_NAV_MENU_COLORS.surface,
    color: TOP_NAV_MENU_COLORS.active,
    text: kaiTypographyWeb.smallBold,
  },
  selectedHover: {
    background: TOP_NAV_MENU_COLORS.selectedHoverBackground,
    color: TOP_NAV_MENU_COLORS.active,
    text: kaiTypographyWeb.smallBold,
  },
  selectedPressed: {
    background: TOP_NAV_MENU_COLORS.selectedPressedBackground,
    color: TOP_NAV_MENU_COLORS.active,
    text: kaiTypographyWeb.smallBold,
  },
} as const satisfies Record<
  InteractiveVisualState,
  { background: string; color: string; text: CSSProperties }
>;

const NAV_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 20,
  height: pxToRem(60),
  minHeight: '60px',
};

const TAB_SLOT_STYLE: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'stretch',
  height: '100%',
};

const MENU_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 10px)',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 160,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: 0,
  border: 0,
  borderRadius: 5,
  background: TOP_NAV_MENU_COLORS.surface,
  boxShadow: '0 4px 3px rgba(29, 29, 29, 0.1), 0 10px 7.5px rgba(29, 29, 29, 0.1)',
  boxSizing: 'border-box',
  overflow: 'hidden',
  zIndex: 1205,
};

const MENU_ITEM_ICON_SLOT_STYLE: CSSProperties = {
  width: 10,
  height: 10,
  minWidth: 10,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const MENU_ITEM_LABEL_BASE_STYLE: CSSProperties = {
  // matches TOP_NAV_MENU_ITEM_STYLES[state].text's font-size (small/smallBold, both 14px):
  // 16px / 14px. This style is always spread after that one in getMenuItemLabelStyle.
  lineHeight: 1.143,
  letterSpacing: '0.28px',
  color: 'currentColor',
  whiteSpace: 'nowrap',
};

const getVisualState = (
  selected: boolean,
  isHovered: boolean,
  isPressed: boolean,
): InteractiveVisualState => {
  if (selected) {
    if (isPressed) return 'selectedPressed';
    if (isHovered) return 'selectedHover';
    return 'selected';
  }

  if (isPressed) return 'pressed';
  if (isHovered) return 'hover';
  return 'default';
};

const getTabStyle = (visualState: InteractiveVisualState): CSSProperties => {
  const style = TOP_NAV_TAB_STYLES[visualState];

  return {
    ...kaiTypographyWeb.xSmallBold,
    lineHeight: 1.375, // matches xSmallBold's font-size (16.5px / 12px)
    letterSpacing: '0px',
    textTransform: 'uppercase',
    color: style.color,
    textDecoration: 'none',
    height: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    border: 0,
    padding: '0 11px',
    margin: '0 -11px',
    boxSizing: 'content-box',
    background: style.background,
    cursor: 'pointer',
  };
};

const SELECTED_INDICATOR_STYLE: CSSProperties = {
  position: 'absolute',
  left: 11,
  right: 11,
  bottom: 0,
  height: 3,
  background: TOP_NAV_COLORS.active,
  pointerEvents: 'none',
};

const getMenuItemStyle = (
  visualState: InteractiveVisualState,
  disabled: boolean,
): CSSProperties => {
  const style = TOP_NAV_MENU_ITEM_STYLES[visualState];

  return {
    width: '100%',
    height: 40,
    border: 0,
    padding: '0 16px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: style.background,
    color: style.color,
    textDecoration: 'none',
    textAlign: 'left',
    boxSizing: 'border-box',
    appearance: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.72 : 1,
  };
};

const getMenuItemLabelStyle = (visualState: InteractiveVisualState): CSSProperties => ({
  ...TOP_NAV_MENU_ITEM_STYLES[visualState].text,
  ...MENU_ITEM_LABEL_BASE_STYLE,
});

const selectedMenuState = (visualState: InteractiveVisualState) =>
  visualState === 'selected'
  || visualState === 'selectedHover'
  || visualState === 'selectedPressed';

const getMenuItemIndicatorStyle = (visualState: InteractiveVisualState): CSSProperties => {
  const style = TOP_NAV_MENU_ITEM_STYLES[visualState];
  const selected = selectedMenuState(visualState);

  return {
    width: 8,
    height: 8,
    borderRadius: 999,
    boxSizing: 'border-box',
    background: selected ? TOP_NAV_MENU_COLORS.active : 'transparent',
    border: selected ? `1px solid ${TOP_NAV_MENU_COLORS.active}` : `1px solid ${style.color}`,
  };
};

const menuItemKey = (item: AdminChromeTopNavItem, menuItem: AdminChromeTopNavMenuItem) => `${item.label}:${menuItem.label}`;

export const AdminChromeTopNavTabs = ({
  items,
  ariaLabel = 'Admin sections',
}: AdminChromeTopNavTabsProps) => {
  const [hoveredItemLabel, setHoveredItemLabel] = useState<string | null>(null);
  const [pressedItemLabel, setPressedItemLabel] = useState<string | null>(null);
  const [openMenuItemLabel, setOpenMenuItemLabel] = useState<string | null>(null);
  const [hoveredMenuItemKey, setHoveredMenuItemKey] = useState<string | null>(null);
  const [pressedMenuItemKey, setPressedMenuItemKey] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!openMenuItemLabel) return;

    const handlePointerDown = (event: globalThis.MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpenMenuItemLabel(null);
        setHoveredMenuItemKey(null);
        setPressedMenuItemKey(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenuItemLabel(null);
        setHoveredMenuItemKey(null);
        setPressedMenuItemKey(null);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuItemLabel]);

  return (
    <nav ref={navRef} style={NAV_STYLE} aria-label={ariaLabel}>
      {items.map(item => {
        const selected = Boolean(item.selected);
        const hasMenu = Boolean(item.menuItems?.length);
        const menuOpen = selected && openMenuItemLabel === item.label;
        const isHovered = hoveredItemLabel === item.label;
        const isPressed = pressedItemLabel === item.label;
        const visualState = getVisualState(selected, isHovered, isPressed);
        const showIndicator = selected && !isHovered && !isPressed;
        const TabElement = item.href && !hasMenu ? 'a' : 'button';

        const handleTabClick = (event: MouseEvent<HTMLElement>) => {
          setPressedItemLabel(null);

          if (hasMenu && selected) {
            event.preventDefault();
            setOpenMenuItemLabel(current => (current === item.label ? null : item.label));
            setHoveredMenuItemKey(null);
            setPressedMenuItemKey(null);
            return;
          }

          setOpenMenuItemLabel(null);
          item.onClick?.();
        };

        return (
          <div key={item.label} style={TAB_SLOT_STYLE}>
            {createElement(
              TabElement,
              {
                href: TabElement === 'a' ? item.href : undefined,
                type: TabElement === 'button' ? 'button' : undefined,
                onClick: handleTabClick,
                onMouseEnter: () => setHoveredItemLabel(item.label),
                onMouseLeave: () => {
                  setHoveredItemLabel(current => (current === item.label ? null : current));
                  setPressedItemLabel(current => (current === item.label ? null : current));
                },
                onMouseDown: () => setPressedItemLabel(item.label),
                onMouseUp: () => setPressedItemLabel(current => (current === item.label ? null : current)),
                onBlur: () => {
                  setHoveredItemLabel(current => (current === item.label ? null : current));
                  setPressedItemLabel(current => (current === item.label ? null : current));
                },
                'aria-current': selected ? 'page' : undefined,
                'aria-haspopup': hasMenu ? 'menu' : undefined,
                'aria-expanded': hasMenu ? menuOpen : undefined,
                'aria-label': item.ariaLabel ?? item.label,
                title: item.title ?? item.label,
                style: {
                  ...getTabStyle(visualState),
                  appearance: TabElement === 'button' ? 'none' : undefined,
                },
              },
              <>
                <span>{item.label}</span>
                {showIndicator
                  ? (
                    <span
                      aria-hidden='true'
                      data-kai-shiki-admin-chrome-top-nav-tab-indicator='true'
                      style={SELECTED_INDICATOR_STYLE}
                    />
                  )
                  : null}
              </>,
            )}

            {hasMenu && menuOpen
              ? (
                <div
                  role='menu'
                  aria-label={item.menuAriaLabel ?? `${item.label} menu`}
                  style={MENU_STYLE}
                >
                  {item.menuItems?.map(menuItem => {
                    const key = menuItemKey(item, menuItem);
                    const disabled = Boolean(menuItem.disabled);
                    const menuItemSelected = Boolean(menuItem.selected);
                    const menuItemVisualState = getVisualState(
                      menuItemSelected,
                      !disabled && hoveredMenuItemKey === key,
                      !disabled && pressedMenuItemKey === key,
                    );
                    const MenuItemElement = menuItem.href && !disabled ? 'a' : 'button';

                    const handleMenuItemClick = (event: MouseEvent<HTMLElement>) => {
                      if (disabled) {
                        event.preventDefault();
                        return;
                      }

                      setOpenMenuItemLabel(null);
                      setHoveredMenuItemKey(null);
                      setPressedMenuItemKey(null);
                      menuItem.onClick?.();
                    };

                    return createElement(
                      MenuItemElement,
                      {
                        key,
                        href: MenuItemElement === 'a' ? menuItem.href : undefined,
                        type: MenuItemElement === 'button' ? 'button' : undefined,
                        role: 'menuitem',
                        disabled: MenuItemElement === 'button' ? disabled : undefined,
                        'aria-disabled': disabled || undefined,
                        tabIndex: disabled && MenuItemElement === 'a' ? -1 : undefined,
                        'aria-current': menuItemSelected ? 'page' : undefined,
                        'aria-label': menuItem.ariaLabel ?? menuItem.label,
                        title: menuItem.title ?? menuItem.label,
                        style: getMenuItemStyle(menuItemVisualState, disabled),
                        onClick: handleMenuItemClick,
                        onMouseEnter: () => {
                          if (!disabled) setHoveredMenuItemKey(key);
                        },
                        onMouseLeave: () => {
                          setHoveredMenuItemKey(current => (current === key ? null : current));
                          setPressedMenuItemKey(current => (current === key ? null : current));
                        },
                        onMouseDown: () => {
                          if (!disabled) setPressedMenuItemKey(key);
                        },
                        onMouseUp: () => setPressedMenuItemKey(current => (current === key ? null : current)),
                        onBlur: () => {
                          setHoveredMenuItemKey(current => (current === key ? null : current));
                          setPressedMenuItemKey(current => (current === key ? null : current));
                        },
                      },
                      <>
                        <span aria-hidden='true' style={MENU_ITEM_ICON_SLOT_STYLE}>
                          <span
                            data-kai-shiki-admin-chrome-top-nav-menu-item-indicator='true'
                            style={getMenuItemIndicatorStyle(menuItemVisualState)}
                          />
                        </span>
                        <span
                          data-kai-shiki-admin-chrome-top-nav-menu-item-label='true'
                          style={getMenuItemLabelStyle(menuItemVisualState)}
                        >
                          {menuItem.label}
                        </span>
                      </>,
                    );
                  })}
                </div>
              )
              : null}
          </div>
        );
      })}
    </nav>
  );
};
